import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faToggleOff, faToggleOn, faTrash, faPlus, faSync, faEraser, faEye } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import AdminTableHeader from '../../../components/Admin/AdminTableHeader';
import AdminActionButton from '../../../components/Admin/AdminActionButton';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import PaginationButtons from '../../../components/Common/Pagination';
import Dialogs from '../../../components/Common/Dialog';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import ProjectTemplateForm from './ProjectTemplateForm';
import {
  ProjectTemplateModel,
  getAdminProjectTemplates,
  saveProjectTemplate,
  updateProjectTemplate,
  deleteProjectTemplate,
  changeProjectTemplateStatus,
  syncProjectTemplateFromWP,
  clearProjectTemplateCache,
} from './services/ProjectTemplate.service';

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '-';
  const date = timestamp._seconds
    ? new Date(timestamp._seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ProjectTemplates = () => {
  const [templates, setTemplates] = useState<ProjectTemplateModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<'status' | 'delete'>('status');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [detailTemplate, setDetailTemplate] = useState<ProjectTemplateModel | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    loadTemplates(currentPage, searchTerm || undefined);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplates = async (page?: number, search?: string) => {
    setLoading(true);
    const result = await getAdminProjectTemplates(page || 1, search);
    setTemplates(result.templates || []);
    setTotalPages(result.totalPages || 0);
    setTotalRecords(result.totalRecords || 0);
    setLoading(false);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    loadTemplates(1, term || undefined);
  };

  const handlePageChange = (page: number | string) => {
    const p = typeof page === 'string' ? parseInt(page, 10) : page;
    if (p > 0 && p <= totalPages) setCurrentPage(p);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const handleEdit = (template: ProjectTemplateModel) => {
    setEditingTemplate({
      id: template.id,
      wpProjectId: template.wpProjectId,
    });
    setShowForm(true);
  };

  const handleSave = async (data: { id?: string; wpProjectId: string }) => {
    if (data.id) {
      await updateProjectTemplate({ id: data.id, wpProjectId: data.wpProjectId }, () => {
        setShowForm(false);
        loadTemplates();
      });
    } else {
      await saveProjectTemplate({ wpProjectId: data.wpProjectId }, () => {
        setShowForm(false);
        loadTemplates();
      });
    }
  };

  const handleToggleStatus = (id: string) => {
    setSelectedId(id);
    setDialogAction('status');
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    setSelectedId(id);
    setDialogAction('delete');
    setOpenDialog(true);
  };

  const handleDialogConfirm = async () => {
    if (!selectedId) return;
    setOpenDialog(false);

    if (dialogAction === 'status') {
      await changeProjectTemplateStatus(selectedId, loadTemplates);
    } else {
      await deleteProjectTemplate(selectedId, loadTemplates);
    }
    setSelectedId(null);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };

  const handleSyncWP = async (id: string) => {
    setSyncingId(id);
    await syncProjectTemplateFromWP(id, loadTemplates);
    setSyncingId(null);
  };

  const handleClearCache = async (id: string) => {
    await clearProjectTemplateCache(id, () => {});
  };

  return (
    <div className="p-0 md:p-4">
      {showForm ? (
        <ProjectTemplateForm
          setShowForm={setShowForm}
          templateDetails={editingTemplate}
          onSave={handleSave}
        />
      ) : (
        <>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            {/* Search (left) */}
            <AdminSearchInput
              placeholder="Search name or WP ID..."
              onSearch={handleSearch}
              className="w-56"
            />
            {/* New Project (right) */}
            <div className="ml-auto">
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-1.5 bg-storyblue text-white rounded-md text-sm font-bold hover:opacity-90"
              >
                <FontAwesomeIcon icon={faPlus} />
                New Project
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                  <AdminTableHeader
                    columns={[
                      { key: 'sn', label: 'S#' },
                      { key: 'name', label: 'Name' },
                      { key: 'wpId', label: 'WP ID' },
                      { key: 'dateList', label: 'Date List' },
                      { key: 'lastSync', label: 'Last Sync' },
                      { key: 'status', label: 'Status' },
                      { key: 'actions', label: 'Actions' },
                    ]}
                  />
                  <tbody>
                    {templates && templates.length > 0 ? (
                      templates.map((template, index) => (
                        <tr key={template.id} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                          <td className="text-left py-2 px-4 border-b">{(currentPage - 1) * 25 + index + 1}</td>
                          <td className="text-left py-2 px-4 border-b">
                            {template.wpTitle || template.name}
                          </td>
                          <td className="text-left py-2 px-4 border-b font-mono text-sm">
                            {template.wpProjectId}
                          </td>
                          <td className="text-left py-2 px-4 border-b text-sm">
                            {template.wpDateList || '-'}
                          </td>
                          <td className="text-left py-2 px-4 border-b text-xs text-gray-500 whitespace-nowrap">
                            {template.wpSyncedAt ? formatDate(template.wpSyncedAt) : 'Never'}
                          </td>
                          <td className="text-left py-2 px-4 border-b capitalize">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                template.isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {template.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-left py-4 px-4 border-b">
                            <div className="flex gap-3">
                              <AdminActionButton
                                icon={faEye}
                                title="View details"
                                hoverColor="hover:text-indigo-600"
                                onClick={() => setDetailTemplate(template)}
                              />
                              <AdminActionButton
                                icon={faPenToSquare}
                                title="Edit WP Project ID"
                                hoverColor="hover:text-blue-600"
                                onClick={() => handleEdit(template)}
                              />
                              <AdminActionButton
                                icon={template.isActive ? faToggleOn : faToggleOff}
                                title={template.isActive ? 'Deactivate' : 'Activate'}
                                className={template.isActive ? 'text-storygreen' : 'text-red-500'}
                                hoverColor={template.isActive ? 'hover:text-green-700' : 'hover:text-red-700'}
                                onClick={() => handleToggleStatus(template.id)}
                              />
                              <AdminActionButton
                                icon={faTrash}
                                title="Delete"
                                hoverColor="hover:text-red-600"
                                onClick={() => handleDelete(template.id)}
                              />
                              <AdminActionButton
                                icon={faSync}
                                title="Sync from WordPress"
                                hoverColor="hover:text-purple-600"
                                spinning={syncingId === template.id}
                                disabled={syncingId !== null}
                                onClick={() => handleSyncWP(template.id)}
                              />
                              <AdminActionButton
                                icon={faEraser}
                                title="Clear resource cache"
                                hoverColor="hover:text-orange-600"
                                onClick={() => handleClearCache(template.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <NoRecordFound colSpan={7} />
                    )}
                  </tbody>
                </table>

                <div className="flex justify-between items-center mt-4">
                  <TableInfoBar
                    currentPage={currentPage}
                    pageSize={25}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    recordsOnPage={templates.length}
                  />
                  {totalPages > 1 && (
                    <PaginationButtons
                      totalPages={totalPages}
                      currentPage={currentPage}
                      handlePageChange={handlePageChange}
                    />
                  )}
                </div>
              </div>
          )}

          {/* Details Modal */}
          {detailTemplate && (
            <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Project Details</h3>
                  <button
                    onClick={() => setDetailTemplate(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  >
                    &times;
                  </button>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-semibold text-gray-600">Name:</span>
                    <p className="text-gray-800">{detailTemplate.wpTitle || detailTemplate.name}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-600">WP Project ID:</span>
                    <p className="font-mono">{detailTemplate.wpProjectId}</p>
                  </div>
                  {detailTemplate.wpExcerpt && (
                    <div>
                      <span className="font-semibold text-gray-600">Excerpt:</span>
                      <p className="text-gray-700">{detailTemplate.wpExcerpt}</p>
                    </div>
                  )}
                  {detailTemplate.wpDateList && (
                    <div>
                      <span className="font-semibold text-gray-600">Date List:</span>
                      <p className="text-gray-700">{detailTemplate.wpDateList}</p>
                    </div>
                  )}
                  {detailTemplate.wpLink && (
                    <div>
                      <span className="font-semibold text-gray-600">WordPress Link:</span>
                      <p>
                        <a
                          href={detailTemplate.wpLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline break-all"
                        >
                          {detailTemplate.wpLink}
                        </a>
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="font-semibold text-gray-600">Status:</span>
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                        detailTemplate.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {detailTemplate.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-600">Last WP Sync:</span>
                    <p className="text-gray-700">
                      {detailTemplate.wpSyncedAt ? formatDate(detailTemplate.wpSyncedAt) : 'Never'}
                    </p>
                  </div>
                  {detailTemplate.createdAt && (
                    <div>
                      <span className="font-semibold text-gray-600">Created:</span>
                      <p className="text-gray-700">{formatDate(detailTemplate.createdAt)}</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setDetailTemplate(null)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          <Dialogs
            openDialog={openDialog}
            handelClose={handleDialogClose}
            confirmMessage={
              dialogAction === 'status'
                ? 'Are you sure you want to change the project status?'
                : 'Are you sure you want to delete this project?'
            }
            handelProcess={handleDialogConfirm}
          />
        </>
      )}
    </div>
  );
};

export default ProjectTemplates;
